import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Users2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { assignableRoles, defaultRoleFor, ROLE_LABELS } from "@/lib/roles";

const SKIP_REASONS = {
  not_found: "no longer exists",
  cannot_modify_self: "you can't modify yourself",
  super_admin_protected: "super admins are protected",
  hierarchy: "above your role",
};

/**
 * Bulk action dialog for the Users table. Renders a different confirmation
 * body depending on `action` (deactivate / reactivate / update_role).
 */
export default function BulkUserActionDialog({ open, onOpenChange, action, userIds, onDone }) {
  const { user: caller } = useAuth();
  const { active } = useClient();
  const clientRoles = active?.roles || null;
  const [role, setRole] = useState(defaultRoleFor(clientRoles));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setRole(defaultRoleFor(clientRoles));
  }, [open]); // eslint-disable-line

  const submit = async () => {
    if (action === "update_role" && !role) return toast.error("Pick a role");
    setSubmitting(true);
    try {
      const payload = { user_ids: userIds, action };
      if (action === "update_role") payload.value = role;
      const { data } = await api.post("/users/bulk-action", payload);
      const skipMsg = data.skipped?.length
        ? ` · ${data.skipped.length} skipped (${data.skipped.map((s) => SKIP_REASONS[s.reason] || s.reason).join(", ")})`
        : "";
      toast.success(`${data.updated} user${data.updated === 1 ? "" : "s"} updated${skipMsg}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    } finally {
      setSubmitting(false);
    }
  };

  const TITLES = {
    deactivate: "Deactivate selected users",
    reactivate: "Reactivate selected users",
    update_role: "Change role for selected users",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-action-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Users2 className="w-4 h-4 text-indigo-500" /> {TITLES[action]}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            This affects <strong>{userIds.length}</strong> user{userIds.length === 1 ? "" : "s"}.
            {action === "deactivate" && " They won't be able to sign in until reactivated. Tickets and audit history stay intact."}
            {action === "reactivate" && " They'll regain sign-in access on their next OTP request."}
            {action === "update_role" && " Permissions update on each user's next API call."}
          </DialogDescription>
        </DialogHeader>

        {action === "update_role" && (
          <div className="space-y-1.5 py-2">
            <Label>New role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="bulk-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {assignableRoles(caller, clientRoles).map((r) => (
                  <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-2 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>Targets above your role, super admins, and yourself will be silently skipped — you'll see the count in the result.</span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className={action === "deactivate" ? "bg-rose-600 hover:bg-rose-700" : "bg-indigo-600 hover:bg-indigo-700"}
            data-testid="bulk-action-confirm"
          >
            {submitting ? "Working..." : (action === "deactivate" ? "Deactivate" : action === "reactivate" ? "Reactivate" : "Apply role change")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
